import * as React from 'react';
import { css } from '@emotion/react';
import { getLanguageDirection } from '../../utils';
import { useEventCallback } from '../../hooks';
import { toPx, useHoneycombTheme } from '../../theming';

export type SliderPosition = {
    x: number;
    y: number;
};

export type SliderPoint = {
    value: number;
    label?: string;
};

export type SliderTheme = {
    railHeight: number;
    handleDiameter: number;
    paddingX: number;
    paddingY: number;
};

const sliderTheme: SliderTheme = {
    railHeight: 10,
    handleDiameter: 36,
    paddingX: 18,
    paddingY: 12,
};

function ascending(a: number, b: number): number {
    return a - b;
}

function validateValue(value: number, min: number, max: number): number {
    return value < min ? min : value > max ? max : value;
}

function trackPointer(event: MouseEvent | React.MouseEvent<HTMLElement>): SliderPosition {
    return {
        x: event.clientX,
        y: event.clientY,
    };
}

function valueToPercentage(value: number, min: number, max: number): number {
    return ((value - min) * 100) / (max - min);
}

function percentageToValue(percent: number, min: number, max: number): number {
    return (max - min) * percent + min;
}

function roundValueToStep(value: number, step: number): number {
    return Math.round(value / step) * step;
}

function findClosestValueIndex(values: number[], newValue: number) {
    const accValue = values.reduce((acc: { diff: number; index: number } | null, value: number, index: number) => {
        const diff = Math.abs(newValue - value);
        if (acc === null || diff <= acc.diff) {
            return {
                diff,
                index,
            };
        }
        return acc;
    }, null);
    return accValue?.index ?? 0;
}

const positionStyle = {
    offset: (percent: number) => {
        return getLanguageDirection() === 'rtl' ? { right: `${percent}%` } : { left: `${percent}%` };
    },
    leap: (percent: number) => ({ width: `${percent}%` }),
};

export type SliderProps = {
    /**
     * A label for the slider handle, either a single string or an array of two strings for a range slider.
     */
    ariaLabel?: string[];

    /**
     * The id of the element which labels the slider.
     */
    ariaLabelledby?: string;

    /**
     * Callback fired when the value of the slider is changed.
     */
    onChange?: (event: React.SyntheticEvent | TouchEvent | MouseEvent, value: number[]) => void;

    /**
     * Callback function that is fired when the `mousedown` event is triggered.
     */
    onMouseDown?: (event: React.MouseEvent<HTMLElement> | null) => void;

    /**
     * Callback function that is fired when the `mouseup` event is triggered.
     */
    onMouseUp?: (event: MouseEvent | null) => void;

    /**
     * Callback function that is fired when the `touchstart` event is triggered.
     */
    onTouchStart?: (event: TouchEvent | null) => void;

    /**
     * Callback function that is fired when the `touchend` event is triggered.
     */
    onTouchEnd?: (event: TouchEvent | null) => void;

    /**
     * Points are predetermined values which can be selected.
     * If `true` the points will be spaced according the value of the `step` prop.
     * @default []
     */
    points?: boolean | SliderPoint[];

    /**
     * The maximum permitted slider value.
     * @default 1000
     */
    max: number;

    /**
     * The minimum permitted slider value.
     * @default 0
     */
    min?: number;

    /**
     * @default slider
     */
    name?: string;

    /**
     * The interval between each selectable value.
     * If null, a custom set of selectable values must be provided using the "points" props.
     * @default 1
     */
    step?: number;

    /**
     * The value of the slider, either a single number or an array of two numbers for a range slider.
     * @default [0, 1000]
     */
    value: number[];

    /**
     * A user-friendly description for the current slider value, either a single string or a function for a range slider.
     */
    valueText: (value: number, index: number) => string | string;
};

/**
 * Slider allow users to make selections from a range of values. It reflects a range of values along a bar with draggable controls to specify a value.
 * We can customise it to allow the selection of a range of values or a singular value along the bar.
 * Limits can be put in place for the selection range via the input props as described below in the table, with default selection range and values provided as fallbacks.
 */
export function Slider(props: SliderProps): React.JSX.Element {
    const {
        ariaLabel: ariaLabelProp,
        ariaLabelledby: ariaLabelledbyProp,
        points: pointsProp = [],
        max = 100,
        min = 0,
        name,
        onChange,
        onMouseDown,
        onMouseUp,
        onTouchStart,
        onTouchEnd,
        step = 1,
        value: valueProp,
        valueText: valueTextProp,
        ...other
    } = props;

    const values = [...valueProp].sort(ascending).map((value) => validateValue(value, min, max));

    // Define points
    const points =
        typeof pointsProp === 'boolean' && pointsProp === true
            ? [...Array(Math.floor((max - min) / step) + 1)].map((_, index) => ({
                  value: min + step * index,
              }))
            : pointsProp;

    const sliderRef = React.useRef<HTMLSpanElement | null>(null);

    const touchId = React.useRef<number>(null);
    const previousIndex = React.useRef<number>(null);

    let axis = 'horizontal';
    if (getLanguageDirection() === 'rtl') {
        axis += '-reverse';
    }

    const trackFinger = useEventCallback((event: TouchEvent): SliderPosition | null => {
        if (touchId.current !== undefined && event.changedTouches) {
            for (let i = 0; i < event.changedTouches.length; i += 1) {
                const touch = event.changedTouches[i];
                if (touch.identifier === touchId.current) {
                    return {
                        x: touch.clientX,
                        y: touch.clientY,
                    };
                }
            }
        }
        return null;
    });

    const focusHandle = useEventCallback((activeIndex: number) => {
        if (
            (sliderRef.current && !sliderRef.current.contains(document.activeElement)) ||
            (document.activeElement && Number(document.activeElement.getAttribute('data-index')) !== activeIndex)
        ) {
            const handle =
                sliderRef.current && (sliderRef.current.querySelector(`[data-index="${activeIndex}"]`) as HTMLElement);
            handle?.focus();
        }
    });

    /**
     * Interaction handlers
     */
    const handleKeyDown = useEventCallback((event: React.KeyboardEvent) => {
        const index = Number(event.currentTarget.getAttribute('data-index'));
        const value = values[index];
        const page = (max - min) / 10;

        let newValue: number = value;

        const { key } = event;
        switch (key) {
            case 'Home':
                newValue = min;
                break;
            case 'End':
                newValue = max;
                break;
            case 'PageUp':
                newValue = value + page;
                break;
            case 'PageDown':
                newValue = value - page;
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                newValue = value + step;
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                newValue = value - step;
                break;
            default:
                return;
        }

        event.preventDefault();
        newValue = roundValueToStep(newValue, step);

        newValue = validateValue(newValue, min, max);

        const newValues = [...values];
        newValues[index] = newValue;
        newValues.sort(ascending);

        focusHandle(newValues.indexOf(newValue));

        if (onChange) {
            onChange(event, newValues);
        }
    });

    const getFingerNewValue = React.useCallback(
        ({ finger, move = false, values: valueInFinger }: any) => {
            const { current: slider } = sliderRef;

            if (!slider) {
                return { newValue: [0], activeIndex: 0 };
            }

            const { width, left } = slider.getBoundingClientRect();

            let percent = (finger.x - left) / width;

            if (axis.indexOf('-reverse') !== -1) {
                percent = 1 - percent;
            }

            let newValue = percentageToValue(percent, min, max);
            newValue = roundValueToStep(newValue, step);

            newValue = validateValue(newValue, min, max);
            let activeIndex = 0;

            if (!move) {
                activeIndex = findClosestValueIndex(valueInFinger, newValue);
            } else {
                activeIndex = previousIndex.current || 0;
            }

            const newValues = [...valueInFinger];
            newValues[activeIndex] = newValue;
            newValues.sort(ascending);

            activeIndex = newValues.indexOf(newValue);
            previousIndex.current = activeIndex;

            return { newValue: newValues, activeIndex };
        },
        [sliderRef, max, min, axis, step]
    );

    const handleTouchMove = useEventCallback((event: TouchEvent) => {
        const finger = trackFinger(event);

        if (!finger) {
            return;
        }

        const { newValue, activeIndex } = getFingerNewValue({
            finger,
            move: true,
            values,
        });

        focusHandle(activeIndex);

        if (onChange) {
            onChange(event, newValue);
        }
    });

    const handleTouchEnd = useEventCallback((event: TouchEvent) => {
        if (onTouchEnd) {
            onTouchEnd(event);
        }
        touchId.current = null;
        document.body.removeEventListener('touchmove', handleTouchMove);
        document.body.removeEventListener('touchend', handleTouchEnd);
    });

    const handleTouchStart = useEventCallback((event: TouchEvent) => {
        if (onTouchStart) {
            onTouchStart(event);
        }

        event.preventDefault();

        const touch = event.changedTouches[0];
        if (touch != null) {
            touchId.current = touch.identifier;
        }
        const finger = trackFinger(event);

        if (!finger) {
            return;
        }

        const { newValue, activeIndex } = getFingerNewValue({
            finger,
            move: false,
            values,
        });

        focusHandle(activeIndex);

        if (onChange) {
            onChange(event, newValue);
        }

        document.body.addEventListener('touchmove', handleTouchMove);
        document.body.addEventListener('touchend', handleTouchEnd);
    });

    const handleMouseMove = useEventCallback((event: MouseEvent) => {
        const finger = trackPointer(event);

        if (!finger) {
            return;
        }

        const { newValue, activeIndex } = getFingerNewValue({
            finger,
            move: true,
            values,
        });

        focusHandle(activeIndex);

        if (onChange) {
            onChange(event, newValue);
        }
    });

    const handleMouseEnter = useEventCallback((event: MouseEvent) => {
        if (event.buttons === 0 && event.relatedTarget !== null) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            handleMouseUp(event);
        }
    });

    const handleMouseUp = useEventCallback((event: MouseEvent) => {
        if (onMouseUp) {
            onMouseUp(event);
        }
        document.body.removeEventListener('mousemove', handleMouseMove);
        document.body.removeEventListener('mouseenter', handleMouseEnter);
        document.body.removeEventListener('mouseup', handleMouseUp);
    });

    const handleMouseDown = useEventCallback((event: React.MouseEvent<HTMLElement>) => {
        if (onMouseDown) {
            onMouseDown(event);
        }

        event.preventDefault();

        const finger = trackPointer(event);

        if (!finger) {
            return;
        }

        const { newValue, activeIndex } = getFingerNewValue({
            finger,
            move: false,
            values,
        });

        focusHandle(activeIndex);

        if (onChange) {
            onChange(event, newValue);
        }
        document.body.addEventListener('mousemove', handleMouseMove);
        document.body.addEventListener('mouseenter', handleMouseEnter);
        document.body.addEventListener('mouseup', handleMouseUp);
    });

    React.useEffect(() => {
        const slider = sliderRef.current;
        slider?.addEventListener('touchstart', handleTouchStart);
        return () => {
            document.body.removeEventListener('mousemove', handleMouseMove);
            document.body.removeEventListener('mouseenter', handleMouseEnter);
            document.body.removeEventListener('mouseup', handleMouseUp);
            document.body.removeEventListener('touchmove', handleTouchMove);
            document.body.removeEventListener('touchend', handleTouchEnd);
            slider?.removeEventListener('touchstart', handleTouchStart);
        };
    }, [
        sliderRef,
        handleTouchStart,
        handleMouseMove,
        handleMouseEnter,
        handleMouseUp,
        handleTouchMove,
        handleTouchEnd,
    ]);

    const getTrackStyle = () => {
        const trackOffset = valueToPercentage(values[0], min, max);
        const trackLeap = valueToPercentage(values[values.length - 1], min, max) - trackOffset;
        return {
            ...positionStyle.offset(trackOffset),
            ...positionStyle.leap(trackLeap),
        };
    };

    const { theme } = useHoneycombTheme();

    const { typography, palette, transitions } = theme;

    const rootStyles = css`
        display: block;
        padding: ${sliderTheme.paddingX}px ${sliderTheme.paddingY}px;
        ${points &&
        `
            padding-bottom: ${
                sliderTheme.paddingX + Number(toPx(typography.variant.body.s.l.fontSize)) * typography.baseLineHeight
            }px;
        `}
    `;

    const innerStyles = css`
        display: flex;
        align-items: center;
        color: ${palette.secondary.base};
        cursor: pointer;
        height: ${sliderTheme.railHeight}px;
        position: relative;
        touch-action: none;
    `;

    const railStyles = css`
        width: 100%;
        background-color: ${palette.background.offset};
        height: ${sliderTheme.railHeight}px;
        position: absolute;
        border-radius: ${sliderTheme.railHeight}px;
    `;

    const sliderTrackStyles = css`
        height: ${sliderTheme.railHeight}px;
        position: absolute;
        border-radius: ${sliderTheme.railHeight}px;
        background-color: ${palette.primary.base};
    `;

    const handleStyles = css`
        width: ${sliderTheme.handleDiameter}px;
        height: ${sliderTheme.handleDiameter}px;
        display: flex;
        outline: 0;
        position: absolute;
        align-items: center;
        margin-left: ${(sliderTheme.handleDiameter / 2) * -1}px;
        border-radius: 50%;
        justify-content: center;
        background-color: ${palette.common.white};
        border: solid 2px ${palette.primary.base};
        transition: box-shadow ${transitions.duration.shortest} ${transitions.easing.easeInOut};
        box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
        &:focus {
            box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
            z-index: 1;
        }
    `;

    const handleBarStyles = css`
        width: 1px;
        height: 12px;
        margin: 0 1.5px;
        background-color: ${palette.primary.base};
    `;

    return (
        <span css={rootStyles} ref={sliderRef} {...other}>
            <span css={innerStyles} onMouseDown={handleMouseDown} role="presentation">
                <span css={railStyles} />
                <span css={sliderTrackStyles} style={getTrackStyle()} />

                <input value={values.join(',')} name={name} type="hidden" />

                {/* Render the points */}
                {Array.isArray(points) &&
                    points.map((point: SliderPoint, index: number) => {
                        const percent = valueToPercentage(point.value, min, max);

                        const style = positionStyle.offset(percent);

                        const active = point.value >= values[0] && point.value <= values[values.length - 1];

                        const start = index === 0;
                        const end = index === points.length - 1;

                        const pointStyles = css`
                            width: 2px;
                            height: 2px;
                            position: absolute;
                            border-radius: 1px;
                            background-color: currentColor;
                            .margin-start(-1px);
                            ${
                                active &&
                                `
                                background-color: #FFF;
                                `
                            }
                        `;

                        const pointLabelStyles = css`
                            top: ${sliderTheme.handleDiameter / 2 + sliderTheme.railHeight / 2 + 8}px;
                            color: ${palette.text.light};
                            opacity: 0.4;
                            position: absolute;
                            font-size: ${typography.variant.body.s.l.fontSize};
                            line-height: 1;
                            white-space: nowrap;
                            transform: translateX(-50%);
                            ${active &&
                            `
                                opacity: 1;
                            `}
                        `;

                        return (
                            <React.Fragment key={index.toString()}>
                                {!start && !end && <span style={style} css={pointStyles} />}
                                <span aria-hidden={true} style={style} css={pointLabelStyles}>
                                    {point.label}
                                </span>
                            </React.Fragment>
                        );
                    })}

                {/* Render the slider handle(s) */}
                {values.map((value, valueIndex) => {
                    const percent = valueToPercentage(value, min, max);
                    const style = positionStyle.offset(percent);

                    const valueText =
                        typeof valueTextProp === 'function'
                            ? valueTextProp(value, valueIndex)
                            : valueTextProp || value?.toString();

                    return (
                        <span
                            key={valueIndex.toString()}
                            css={handleStyles}
                            tabIndex={0}
                            role="slider"
                            style={style}
                            data-index={valueIndex}
                            aria-label={ariaLabelProp?.[valueIndex]}
                            aria-labelledby={ariaLabelledbyProp}
                            aria-orientation="horizontal"
                            aria-valuemax={max}
                            aria-valuemin={min}
                            aria-valuenow={value}
                            aria-valuetext={valueText}
                            data-id={`slider-handle-${valueIndex}`}
                            onKeyDown={handleKeyDown}
                        >
                            {[1, 2, 3].map((index) => (
                                <span key={index.toString()} css={handleBarStyles} />
                            ))}
                        </span>
                    );
                })}
            </span>
        </span>
    );
}
